import React, { useState } from "react";
import { DashboardLayout } from "@/components/Layout/DashboardLayout";
import api from "@/lib/api";
import { adminNavItems } from "./_nav";
import { useLang } from "@/contexts/LanguageContext";
import { UserPlus, Loader2, Check, Copy, ChevronRight, KeyRound } from "lucide-react";

const BUSINESS_TYPES = [
  { key: "restaurant",     label: "Restaurant",         emoji: "🍽️" },
  { key: "cafe",           label: "Café",               emoji: "☕" },
  { key: "grocery",        label: "Grocery Store",      emoji: "🛒" },
  { key: "pharmacy",       label: "Pharmacy",           emoji: "💊" },
  { key: "home_services",  label: "Home Services",      emoji: "🔧" },
  { key: "hotels",         label: "Hotel",              emoji: "🏨" },
  { key: "flats",          label: "Flat / Rental",      emoji: "🏢" },
  { key: "courier",        label: "Courier Company",    emoji: "📦" },
  { key: "business",       label: "Business Services",  emoji: "💼" },
];

const STATES = [
  "khartoum", "red_sea", "kassala", "al_gazira", "sennar", "white_nile", "blue_nile",
  "north_kordofan", "south_kordofan", "west_kordofan",
  "north_darfur", "south_darfur", "east_darfur", "west_darfur", "central_darfur",
  "river_nile", "northern", "al_qadarif",
];

export default function AdminCreateBusiness() {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", business_type: "restaurant", email: "", password: "",
    phone: "", city: "khartoum", state: "khartoum", description: "",
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [menuMsg, setMenuMsg] = useState("");
  const [menuLoading, setMenuLoading] = useState(false);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const autoMenu = async () => {
    if (!result?.business_id) return;
    setMenuLoading(true); setMenuMsg("");
    try {
      const { data } = await api.post(`/admin/restaurants/${result.business_id}/auto-menu`);
      setMenuMsg(`✅ Added ${data.added} common menu items${data.skipped ? ` (${data.skipped} already existed)` : ""}.`);
    } catch (e) {
      setMenuMsg(e?.response?.data?.detail || "Failed to generate menu");
    } finally { setMenuLoading(false); }
  };

  const generatePw = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    const pw = Array.from({length: 10}, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    setForm(f => ({ ...f, password: pw }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/businesses/create-account", form);
      setResult(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Failed to create business account");
    } finally {
      setLoading(false);
    }
  };

  const copyAll = () => {
    if (!result) return;
    const text = `ServeOn Business Account\n\nBusiness: ${result.business_name}\nEmail: ${result.credentials.email}\nPassword: ${result.credentials.password}\nLogin: ${result.credentials.login_url}\n\nWelcome to ServeOn!`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const reset = () => {
    setResult(null); setErr("");
    setForm({ name: "", business_type: "restaurant", email: "", password: "",
              phone: "", city: "khartoum", state: "khartoum", description: "" });
  };

  return (
    <DashboardLayout items={adminNavItems(t)} title="Create Business">
      <div className="max-w-3xl mx-auto py-8 px-4" data-testid="admin-create-business-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[var(--so-orange)]/15 text-[var(--so-orange)] flex items-center justify-center">
            <UserPlus className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold">Create Business Account</h1>
            <p className="text-sm text-[var(--so-text-soft)]">Manually create login credentials for restaurants, cafés, groceries, and other businesses you onboard.</p>
          </div>
        </div>

        {result ? (
          <div className="so-card p-6" data-testid="business-created-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-600 flex items-center justify-center mb-3">
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-xl font-bold">Account created!</h2>
            <p className="text-sm text-[var(--so-text-soft)] mt-1">{result.message}</p>

            <div className="mt-5 p-4 rounded-xl bg-[var(--so-orange)]/5 border-2 border-[var(--so-orange)]/30 space-y-2 font-mono text-sm" data-testid="business-credentials-box">
              <div><span className="text-[var(--so-text-soft)]">Business:</span> <b>{result.business_name}</b></div>
              <div><span className="text-[var(--so-text-soft)]">Type:</span> <b>{result.business_type}</b></div>
              <div><span className="text-[var(--so-text-soft)]">Email:</span> <b data-testid="cred-email">{result.credentials.email}</b></div>
              <div><span className="text-[var(--so-text-soft)]">Password:</span> <b data-testid="cred-password">{result.credentials.password}</b></div>
              <div><span className="text-[var(--so-text-soft)]">Login URL:</span> <a href={result.credentials.login_url} target="_blank" rel="noopener noreferrer" className="text-[var(--so-orange)] underline">{result.credentials.login_url}</a></div>
            </div>

            <div className="flex gap-3 mt-5">
              <button onClick={copyAll} className="so-btn-primary flex-1 justify-center inline-flex items-center gap-2" data-testid="copy-credentials">
                {copied ? <><Check className="w-4 h-4"/> Copied</> : <><Copy className="w-4 h-4"/> Copy to clipboard</>}
              </button>
              <button onClick={reset} className="so-btn-secondary flex-1 justify-center" data-testid="create-another">Create another</button>
            </div>

            {(result.business_type === "restaurant" || result.business_type === "cafe") && (
              <div className="mt-5 pt-5 border-t border-[var(--so-border)]">
                <p className="text-sm font-semibold mb-1">Menu setup</p>
                <p className="text-xs text-[var(--so-text-soft)] mb-3">Instantly fill this restaurant with a ready-made menu of common mainstream items (burgers, shawarma, pizza, fried chicken, juices…). The owner can edit prices anytime.</p>
                <button onClick={autoMenu} disabled={menuLoading} className="so-btn-primary w-full justify-center inline-flex items-center gap-2" data-testid="auto-menu-btn">
                  {menuLoading ? <Loader2 className="w-4 h-4 animate-spin"/> : "🍔 Auto-generate common menu"}
                </button>
                {menuMsg && <div className="mt-3 text-sm rounded-lg p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 dark:text-emerald-400" data-testid="auto-menu-result">{menuMsg}</div>}
              </div>
            )}
          </div>
        ) : (
          <form onSubmit={submit} className="so-card p-6 space-y-4" data-testid="create-business-form">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">Business Name *</label>
                <input value={form.name} onChange={set("name")} required className="so-input mt-1" placeholder="e.g. Kabab Palace" data-testid="input-name"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">Type *</label>
                <select value={form.business_type} onChange={set("business_type")} className="so-input mt-1" data-testid="input-type">
                  {BUSINESS_TYPES.map(t => <option key={t.key} value={t.key}>{t.emoji} {t.label}</option>)}
                </select>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">Owner Email *</label>
                <input type="email" value={form.email} onChange={set("email")} required className="so-input mt-1" placeholder="owner@example.com" data-testid="input-email"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)] flex justify-between items-center">
                  <span>Password *</span>
                  <button type="button" onClick={generatePw} className="text-[var(--so-orange)] text-[10px] hover:underline inline-flex items-center gap-1" data-testid="generate-password">
                    <KeyRound className="w-3 h-3"/> Generate
                  </button>
                </label>
                <input value={form.password} onChange={set("password")} required minLength={6} className="so-input mt-1 font-mono" placeholder="min 6 characters" data-testid="input-password"/>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">Phone</label>
                <input value={form.phone} onChange={set("phone")} className="so-input mt-1" placeholder="+249xxxxxxxxx" dir="ltr" data-testid="input-phone"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">City</label>
                <input value={form.city} onChange={set("city")} className="so-input mt-1" placeholder="khartoum" data-testid="input-city"/>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">State</label>
                <select value={form.state} onChange={set("state")} className="so-input mt-1" data-testid="input-state">
                  {STATES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-[var(--so-text-soft)]">Description (optional)</label>
              <textarea value={form.description} onChange={set("description")} rows={2} className="so-input mt-1" placeholder="Short business description" data-testid="input-description"/>
            </div>

            {err && <div className="text-sm text-red-500 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="create-business-error">{err}</div>}

            <button type="submit" disabled={loading} className="so-btn-primary w-full justify-center inline-flex items-center gap-2" data-testid="submit-create-business">
              {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <ChevronRight className="w-4 h-4"/>}
              Create business account
            </button>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
}
